import type {
  PortalTaskFieldOutcome,
  PortalTaskFieldWrite,
} from '@/modules/client-portal/task-field-write.api'
import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { getClientPortalContextFromPathname } from '@/lib/api/client-portal-context'
import { useLocation } from 'react-router-dom'

import { SessionChangedError } from '@scrambl/shared/session-scope'

interface ClientPortalContextValue {
  isPortalView: boolean
  entityId: string | null
  projectId: string | null
  organizationId: string | null
  allowStatusUpdate: boolean
  allowPriorityUpdate: boolean
  allowDueDateUpdate: boolean
  allowDescriptionUpdate: boolean
  editableCustomFieldIds: string[]
  updateTaskStatus: (
    taskId: string,
    statusId: string,
    statusTypeId: string,
    statusComment?: string,
  ) => Promise<void>
  updateTaskPriority: (taskId: string, priority: string | null) => Promise<void>
  updateTaskDueDate: (
    taskId: string,
    dueDate: Date | string | null,
    dueDateHasTime?: boolean,
  ) => Promise<void>
  updateTaskDescription: (taskId: string, description: string) => Promise<void>
  updateTaskCustomField: (
    taskId: string,
    fieldId: string,
    value: unknown,
  ) => Promise<void>
}

const ClientPortalContext = createContext<ClientPortalContextValue | null>(null)

interface ClientPortalProviderProps {
  children: ReactNode
  write: (input: PortalTaskFieldWrite) => Promise<PortalTaskFieldOutcome>
  entityId: string
  projectId: string
  organizationId?: string | null
  allowStatusUpdate?: boolean
  allowPriorityUpdate?: boolean
  allowDueDateUpdate?: boolean
  allowDescriptionUpdate?: boolean
  editableCustomFieldIds?: string[]
}

export function ClientPortalProvider({
  children,
  write,
  entityId,
  projectId,
  organizationId = null,
  allowStatusUpdate = false,
  allowPriorityUpdate = false,
  allowDueDateUpdate = false,
  allowDescriptionUpdate = false,
  editableCustomFieldIds = [],
}: ClientPortalProviderProps) {
  const requireCapability = (allowed: boolean) => {
    if (!allowed) throw new SessionChangedError()
  }
  const updateTaskStatus = async (
    taskId: string,
    statusId: string,
    statusTypeId: string,
    statusComment?: string,
  ) => {
    requireCapability(allowStatusUpdate)
    await write({
      kind: 'status',
      taskId,
      statusId,
      statusTypeId,
      statusComment,
    })
  }
  const updateTaskPriority = async (
    taskId: string,
    priority: string | null,
  ) => {
    requireCapability(allowPriorityUpdate)
    await write({ kind: 'priority', taskId, value: priority })
  }
  const updateTaskDueDate = async (
    taskId: string,
    dueDate: Date | string | null,
    dueDateHasTime?: boolean,
  ) => {
    requireCapability(allowDueDateUpdate)
    await write({
      kind: 'due-date',
      taskId,
      value: dueDate instanceof Date ? dueDate.toISOString() : dueDate,
      hasTime: dueDateHasTime,
    })
  }
  const updateTaskDescription = async (taskId: string, description: string) => {
    requireCapability(allowDescriptionUpdate)
    await write({ kind: 'description', taskId, value: description })
  }
  const updateTaskCustomField = async (
    taskId: string,
    fieldId: string,
    value: unknown,
  ) => {
    requireCapability(editableCustomFieldIds.includes(fieldId))
    await write({ kind: 'custom', taskId, fieldId, value })
  }

  const value: ClientPortalContextValue = {
    isPortalView: true,
    entityId,
    projectId,
    organizationId,
    allowStatusUpdate,
    allowPriorityUpdate,
    allowDueDateUpdate,
    allowDescriptionUpdate,
    editableCustomFieldIds,
    updateTaskStatus,
    updateTaskPriority,
    updateTaskDueDate,
    updateTaskDescription,
    updateTaskCustomField,
  }

  return (
    <ClientPortalContext.Provider value={value}>
      {children}
    </ClientPortalContext.Provider>
  )
}

export function useClientPortal(): ClientPortalContextValue | null {
  return useContext(ClientPortalContext)
}

export function useIsPortalView(): boolean {
  const context = useContext(ClientPortalContext)
  const location = useLocation()
  // The provider (ClientPortalProvider, mounted by the portal task table) is the
  // primary signal, but portal pages that reuse shared components without it —
  // e.g. the discussion thread view — are still reliably detectable from the
  // /client-portal/:entityId URL (the same signal the API layer uses to route
  // to portal endpoints). Without this fallback, staff-only chrome (comment
  // visibility badge + composer toggle) would leak into those portal pages.
  if (context?.isPortalView) return true
  return !!getClientPortalContextFromPathname(location.pathname)
}
